import React from "react";
import {
  SimpleForm,
  required,
  TextInput,
  SelectInput,
  FormDataConsumer,
} from "react-admin";
import { useForm } from "react-final-form";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { FormToolbar } from "../../common/form/FormToolbar";
import { DeviceType, Vendor } from "../../../common/data/service-proxies";
import {
  geometrisDeviceTypes,
  iosixDeviceTypes,
  pacificTrackDeviceTypes,
} from "../../../static/eldDeviceTypes";
import "../../common/styles/form-style.css";

interface Props {}

const VendorSelectInput = () => {
  const form = useForm();

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <SelectInput
          {...rest}
          source="vendorType"
          label="resources.admin-elds.labels.vendor"
          choices={[
            { id: Vendor.Geometris, name: "Geometris" },
            { id: Vendor.PacificTrack, name: "Pacific Track" },
            { id: Vendor.Iosix, name: "iOSIX" },
          ]}
          validate={required()}
          variant="outlined"
          onChange={(event) => {
            form.change("deviceType", undefined);
            const newVendor = event?.target?.value;
            if (newVendor === Vendor.Geometris) {
              form.change("macAddress", undefined);
              form.change("deviceName", undefined);
            } else if (newVendor === Vendor.Iosix) {
              form.change("serialNumber", undefined);
              form.change("deviceName", undefined);
              form.change("deviceType", DeviceType.Io1020);
            }
          }}
        />
      )}
    </FormDataConsumer>
  );
};

const DeviceTypeSelectInput = () => {
  const form = useForm();

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => {
        const vendorType = formData?.vendorType;

        let deviceTypeOptions: Array<{
          id: DeviceType;
          name: string;
        }> = [];
        if (vendorType === Vendor.Geometris) {
          deviceTypeOptions = geometrisDeviceTypes;
        } else if (vendorType === Vendor.PacificTrack) {
          deviceTypeOptions = pacificTrackDeviceTypes;
        } else if (vendorType === Vendor.Iosix) {
          deviceTypeOptions = iosixDeviceTypes;
        }

        return (
          <SelectInput
            {...rest}
            source="deviceType"
            label="resources.admin-elds.labels.deviceType"
            choices={deviceTypeOptions}
            validate={required()}
            variant="outlined"
            onChange={(event: any) => {
              const newDeviceType = event?.target?.value;
              if (vendorType === Vendor.PacificTrack) {
                if (newDeviceType !== DeviceType.PacificTrack30) {
                  form.change("macAddress", undefined);
                }
                if (newDeviceType !== DeviceType.PacificTrack40) {
                  form.change("deviceName", undefined);
                }
              }
            }}
          />
        );
      }}
    </FormDataConsumer>
  );
};

export const EldForm = (props: Props) => {
  return (
    <SimpleForm
      {...props}
      variant="outlined"
      className="form-create-edit"
      toolbar={<FormToolbar showDeleteButton={false} />}
    >
      <ProtectedElement source="vendorType">
        <VendorSelectInput />
      </ProtectedElement>
      <ProtectedElement source="deviceType">
        <DeviceTypeSelectInput />
      </ProtectedElement>
      <FormDataConsumer>
        {({ formData, ...rest }) => {
          const vendor = formData?.vendorType;
          const deviceType = formData?.deviceType;

          const showSerialNumber =
            vendor && deviceType && vendor !== Vendor.Iosix;

          if (!showSerialNumber) {
            return null;
          }

          return (
            <ProtectedElement source="serialNumber">
              <TextInput
                {...rest}
                source="serialNumber"
                label="resources.admin-elds.labels.serialNumber"
                validate={required()}
              />
            </ProtectedElement>
          );
        }}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData, ...rest }) => {
          const vendor = formData?.vendorType;
          const deviceType = formData?.deviceType;

          if (
            vendor === Vendor.Iosix ||
            (vendor === Vendor.PacificTrack &&
              deviceType === DeviceType.PacificTrack30)
          ) {
            return (
              <ProtectedElement source="macAddress">
                <TextInput
                  source="macAddress"
                  label="resources.admin-elds.labels.macAddress"
                  validate={required()}
                  {...rest}
                />
              </ProtectedElement>
            );
          }

          if (
            vendor === Vendor.PacificTrack &&
            deviceType === DeviceType.PacificTrack40
          ) {
            return (
              <ProtectedElement source="deviceName">
                <TextInput
                  source="deviceName"
                  label="resources.admin-elds.labels.deviceName"
                  validate={required()}
                  {...rest}
                />
              </ProtectedElement>
            );
          }

          return null;
        }}
      </FormDataConsumer>
    </SimpleForm>
  );
};
